import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Base from "../core/Base";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, InputAdornment, Chip,
  Stack, Button, Tabs, Tab, Grid, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  FormControl, InputLabel, Switch, FormControlLabel, CircularProgress,
  Alert, Skeleton,
} from "@mui/material";
import SearchIcon           from "@mui/icons-material/Search";
import AddIcon              from "@mui/icons-material/Add";
import EditIcon             from "@mui/icons-material/Edit";
import DeleteIcon           from "@mui/icons-material/Delete";
import RssFeedIcon          from "@mui/icons-material/RssFeed";
import GridViewIcon         from "@mui/icons-material/GridView";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PlayArrowIcon        from "@mui/icons-material/PlayArrow";
import PauseIcon            from "@mui/icons-material/Pause";
import RefreshIcon          from "@mui/icons-material/Refresh";
import SyncIcon             from "@mui/icons-material/Sync";
import FileDownloadIcon     from "@mui/icons-material/FileDownload";
import FileUploadIcon       from "@mui/icons-material/FileUpload";
import { isAutheticated } from "../auth/helper";
import {
  getFeedGroups, createFeedGroup, updateFeedGroup, deleteFeedGroup,
  getFeeds, deleteFeed, toggleFeed,
  fetchNow, toggleGroupFeeds, toggleIngestion, getSettings, reloadScheduler,
  exportFeeds, importFeeds,
} from "./helper/adminapicall";

const GROUP_COLORS = [
  "#ef4444","#f97316","#eab308","#22c55e",
  "#14b8a6","#3b82f6","#8b5cf6","#ec4899",
  "#0ea5e9","#84cc16","#6b7280","#f43f5e",
];

const POLL_OPTIONS = [
  { label: "Inherit (global/group)",  value: null },
  { label: "5 min",  value: 300  }, { label: "10 min", value: 600  },
  { label: "15 min", value: 900  }, { label: "30 min", value: 1800 },
  { label: "1 hour", value: 3600 }, { label: "2 hours",value: 7200 },
];

function fromNow(d) {
  if (!d) return "Never";
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m/60)}h ago`;
  return `${Math.floor(m/1440)}d ago`;
}
function fmtInterval(s) {
  if (!s) return "Global";
  if (s < 60)   return `${s}s`;
  if (s < 3600) return `${s/60}m`;
  return `${s/3600}h`;
}

const EMPTY_GROUP = { name: "", description: "", color: "#3b82f6", poll_interval_seconds: null };

const FeedSources = () => {
  const { admin, token } = isAutheticated();
  const history = useHistory();
  const [tab, setTab] = useState(0);

  // ── Groups state ─────────────────────────────────────────────────────────────
  const [groups,       setGroups]       = useState([]);
  const [loadingGroups,setLoadingGroups]= useState(true);
  const [grpDialog,    setGrpDialog]    = useState(false);
  const [editGrp,      setEditGrp]      = useState(null); // null = add mode
  const [grpForm,      setGrpForm]      = useState(EMPTY_GROUP);
  const [savingGrp,    setSavingGrp]    = useState(false);
  const [delGrp,       setDelGrp]       = useState(null);
  const [deletingGrp,  setDeletingGrp]  = useState(false);
  const [reassignTo,   setReassignTo]   = useState("");

  // ── Feeds state ───────────────────────────────────────────────────────────────
  const [feeds,       setFeeds]       = useState([]);
  const [feedTotal,   setFeedTotal]   = useState(0);
  const [loadingFeeds,setLoadingFeeds]= useState(true);
  const [feedSearch,  setFeedSearch]  = useState("");
  const [feedGroup,   setFeedGroup]   = useState("");
  const [feedActive,  setFeedActive]  = useState("");
  const [feedPage,    setFeedPage]    = useState(0);
  const [delFeed,     setDelFeed]     = useState(null);
  const [deletingFeed,setDeletingFeed]= useState(false);
  const PAGE_SIZE = 50;

  const [ingestionPaused,   setIngestionPaused]   = useState(false);
  const [togglingIngestion, setTogglingIngestion] = useState(false);
  const [reloading,         setReloading]         = useState(false);
  const [fetchingFeed,      setFetchingFeed]      = useState(null);
  const [togglingGroup,     setTogglingGroup]     = useState(null);
  const [exporting,         setExporting]         = useState(false);
  const [importDialog,      setImportDialog]      = useState(false);
  const [importData,        setImportData]        = useState(null);
  const [importing,         setImporting]         = useState(false);

  const [msg, setMsg] = useState({ text: "", severity: "success" });
  const flash = (text, severity="success") => { setMsg({text,severity}); setTimeout(()=>setMsg({text:""}),4000); };

  // ── Load ──────────────────────────────────────────────────────────────────────
  const loadGroups = useCallback(() => {
    setLoadingGroups(true);
    getFeedGroups(admin._id, token).then(d => setGroups(Array.isArray(d) ? d : [])).finally(()=>setLoadingGroups(false));
  }, [admin._id, token]);

  const loadFeeds = useCallback(() => {
    setLoadingFeeds(true);
    let qs = `?page=${feedPage+1}&limit=${PAGE_SIZE}`;
    if (feedGroup)  qs += `&group_id=${feedGroup}`;
    if (feedActive) qs += `&is_active=${feedActive}`;
    getFeeds(admin._id, token, qs).then(d => {
      setFeeds(Array.isArray(d?.feeds) ? d.feeds : []);
      setFeedTotal(d?.total || 0);
    }).finally(()=>setLoadingFeeds(false));
  }, [admin._id, token, feedPage, feedGroup, feedActive]);

  useEffect(() => {
    loadGroups();
    getSettings(admin._id, token).then(d => {
      if (d && typeof d.ingestion_paused === "boolean") setIngestionPaused(d.ingestion_paused);
    });
  }, [loadGroups, admin._id, token]);
  useEffect(() => { loadFeeds(); }, [loadFeeds]);

  // ── Group CRUD ────────────────────────────────────────────────────────────────
  const openAddGroup = () => { setEditGrp(null); setGrpForm(EMPTY_GROUP); setGrpDialog(true); };
  const openEditGroup = (g) => {
    setEditGrp(g);
    setGrpForm({ name: g.name, description: g.description||"", color: g.color, poll_interval_seconds: g.poll_interval_seconds });
    setGrpDialog(true);
  };
  const saveGroup = async () => {
    if (!grpForm.name.trim()) return;
    setSavingGrp(true);
    const fn = editGrp ? updateFeedGroup(admin._id, token, editGrp._id, grpForm) : createFeedGroup(admin._id, token, grpForm);
    const d  = await fn;
    setSavingGrp(false);
    if (d?.error) return flash(d.error, "error");
    flash(editGrp ? "Group updated" : "Group created");
    setGrpDialog(false);
    loadGroups();
  };
  const confirmDeleteGroup = async () => {
    setDeletingGrp(true);
    let qs = delGrp.feed_count > 0
      ? (reassignTo ? `?reassign_to=${reassignTo}` : "?force=true")
      : "";
    const d = await deleteFeedGroup(admin._id, token, delGrp._id, qs);
    setDeletingGrp(false);
    if (d?.error) return flash(d.error, "error");
    flash("Group deleted");
    setDelGrp(null); setReassignTo("");
    loadGroups(); loadFeeds();
  };

  // ── Scheduler reload ──────────────────────────────────────────────────────────
  const handleReloadScheduler = async () => {
    setReloading(true);
    const d = await reloadScheduler(admin._id, token);
    setReloading(false);
    if (d?.error) return flash(d.error, "error");
    flash("Scheduler resynced — all active feeds re-queued");
  };

  // ── Global ingestion toggle ───────────────────────────────────────────────────
  const handleToggleIngestion = async () => {
    setTogglingIngestion(true);
    const d = await toggleIngestion(admin._id, token);
    setTogglingIngestion(false);
    if (d?.error) return flash(d.error, "error");
    setIngestionPaused(d.ingestion_paused);
    flash(d.ingestion_paused ? "All polling paused" : "Polling resumed");
  };

  // ── Export ────────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    const data = await exportFeeds(admin._id, token);
    setExporting(false);
    if (data?.error) return flash(data.error, "error");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `feeds-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    flash(`Exported ${data.count} feeds`);
  };

  // ── Import ────────────────────────────────────────────────────────────────────
  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target.result);
        if (!Array.isArray(data?.feeds)) throw new Error("Missing feeds array");
        setImportData(data);
        setImportDialog(true);
      } catch {
        flash("Invalid file — expected a feeds JSON export", "error");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImportConfirm = async () => {
    setImporting(true);
    const d = await importFeeds(admin._id, token, importData);
    setImporting(false);
    if (d?.error) return flash(d.error, "error");
    flash(
      `Import done — ${d.created} created, ${d.skipped} skipped${d.errors ? `, ${d.errors} errors` : ""}`,
      d.errors ? "warning" : "success"
    );
    setImportDialog(false);
    setImportData(null);
    loadFeeds(); loadGroups();
  };

  // ── Group toggle ──────────────────────────────────────────────────────────────
  const handleToggleGroup = async (g) => {
    const allActive = g.active_count === g.feed_count && g.feed_count > 0;
    const next = !allActive;
    setTogglingGroup(g._id);
    const d = await toggleGroupFeeds(admin._id, token, g._id, next);
    setTogglingGroup(null);
    if (d?.error) return flash(d.error, "error");
    flash(next ? `"${g.name}" feeds enabled` : `"${g.name}" feeds paused`);
    loadGroups(); loadFeeds();
  };

  // ── Feed actions ──────────────────────────────────────────────────────────────
  const handleToggleFeed = async (f) => {
    const d = await toggleFeed(admin._id, token, f._id);
    if (d?.error) return flash(d.error, "error");
    setFeeds(prev => prev.map(x => x._id === f._id ? {...x, is_active: d.is_active} : x));
  };
  const handleFetchNow = async (f) => {
    setFetchingFeed(f._id);
    const d = await fetchNow(admin._id, token, f._id);
    setFetchingFeed(null);
    if (d?.error) return flash(d.error, "error");
    flash(`"${f.title}" fetch triggered`);
  };

  const confirmDeleteFeed = async () => {
    setDeletingFeed(true);
    const d = await deleteFeed(admin._id, token, delFeed._id);
    setDeletingFeed(false);
    if (d?.error) return flash(d.error, "error");
    flash("Feed deleted");
    setDelFeed(null);
    loadFeeds(); loadGroups();
  };

  // Filtered feeds client-side by search
  const visibleFeeds = feedSearch
    ? feeds.filter(f => f.title?.toLowerCase().includes(feedSearch.toLowerCase()) || f.url?.toLowerCase().includes(feedSearch.toLowerCase()))
    : feeds;

  return (
    <Base>
      {msg.text && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={()=>setMsg({text:""})}>{msg.text}</Alert>}

      <Box sx={{ mb: 3, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5">Feed Sources</Typography>
          <Typography variant="body2" color="text.secondary">Manage RSS/Atom feed groups and sources</Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip title="Export all feeds to JSON">
            <Button variant="outlined" size="small" color="inherit"
              startIcon={exporting ? <CircularProgress size={14} color="inherit"/> : <FileDownloadIcon/>}
              onClick={handleExport} disabled={exporting}>
              Export
            </Button>
          </Tooltip>
          <Tooltip title="Import feeds from JSON export">
            <Button variant="outlined" size="small" color="inherit" component="label"
              startIcon={<FileUploadIcon/>}>
              Import
              <input type="file" accept=".json,application/json" hidden onChange={handleImportFile}/>
            </Button>
          </Tooltip>
          <Tooltip title="Resync scheduler — re-reads all active feeds from DB. Use if feeds stopped polling unexpectedly.">
            <Button variant="outlined" size="small" color="inherit"
              startIcon={reloading ? <CircularProgress size={14} color="inherit"/> : <SyncIcon/>}
              onClick={handleReloadScheduler} disabled={reloading}>
              Sync
            </Button>
          </Tooltip>
          <Tooltip title={ingestionPaused ? "Resume all polling" : "Pause all polling"}>
            <Button
              variant={ingestionPaused ? "contained" : "outlined"}
              color={ingestionPaused ? "success" : "warning"}
              size="small"
              startIcon={togglingIngestion ? <CircularProgress size={14} color="inherit"/> : ingestionPaused ? <PlayArrowIcon/> : <PauseIcon/>}
              onClick={handleToggleIngestion}
              disabled={togglingIngestion}
            >
              {ingestionPaused ? "Resume Ingestion" : "Pause Ingestion"}
            </Button>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => tab === 0 ? openAddGroup() : history.push("/add/feed")}>
            {tab === 0 ? "Add Group" : "Add Feed"}
          </Button>
        </Stack>
      </Box>

      {/* Stats bar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label:"Groups",       value: groups.length,                          color:"#8b5cf6" },
          { label:"Total Feeds",  value: groups.reduce((s,g)=>s+(g.feed_count||0),0), color:"#3b82f6" },
          { label:"Active Feeds", value: groups.reduce((s,g)=>s+(g.active_count||0),0), color:"#22c55e" },
          { label:"Inactive",     value: groups.reduce((s,g)=>s+(g.feed_count||0),0) - groups.reduce((s,g)=>s+(g.active_count||0),0), color:"#f59e0b" },
        ].map(s => (
          <Grid item xs={6} md={3} key={s.label}>
            <Paper elevation={0} sx={{ p:1.5, border:"1px solid", borderColor:"divider", borderRadius:2 }}>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color:s.color }}>{loadingGroups ? "—" : s.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{ mb:2, borderBottom:"1px solid", borderColor:"divider" }}>
        <Tab label="Groups"  icon={<GridViewIcon sx={{fontSize:16}}/>} iconPosition="start"/>
        <Tab label="Feeds"   icon={<RssFeedIcon sx={{fontSize:16}}/>}  iconPosition="start"/>
      </Tabs>

      {/* ── GROUPS TAB ──────────────────────────────────────────────────────── */}
      {tab === 0 && (
        loadingGroups
          ? <Grid container spacing={2}>{[...Array(6)].map((_,i)=><Grid item xs={12} sm={6} md={4} key={i}><Skeleton variant="rounded" height={110}/></Grid>)}</Grid>
          : groups.length === 0
            ? <Box sx={{py:8, textAlign:"center"}}>
                <GridViewIcon sx={{fontSize:48, color:"text.disabled", opacity:.3}}/>
                <Typography color="text.secondary" sx={{mt:1}}>No groups yet</Typography>
                <Button variant="outlined" size="small" startIcon={<AddIcon/>} sx={{mt:2}} onClick={openAddGroup}>Create First Group</Button>
              </Box>
            : <Grid container spacing={2}>
                {groups.map(g => (
                  <Grid item xs={12} sm={6} md={4} key={g._id}>
                    <Paper elevation={0} sx={{
                      p:2, border:"1px solid", borderColor:"divider", borderRadius:2,
                      borderLeft:"4px solid", borderLeftColor:g.color||"primary.main",
                      position:"relative",
                    }}>
                      <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"flex-start"}}>
                        <Box sx={{flex:1, minWidth:0}}>
                          <Typography fontWeight={700} noWrap>{g.name}</Typography>
                          {g.description && <Typography variant="caption" color="text.secondary" display="block" noWrap>{g.description}</Typography>}
                        </Box>
                        <Box sx={{display:"flex", gap:0.5, ml:1, flexShrink:0}}>
                          {g.feed_count > 0 && (
                            <Tooltip title={g.active_count === g.feed_count ? "Pause all feeds" : "Resume all feeds"}>
                              <IconButton size="small"
                                onClick={() => handleToggleGroup(g)}
                                disabled={togglingGroup === g._id}
                                sx={{ color: g.active_count === g.feed_count ? "warning.main" : "success.main" }}>
                                {togglingGroup === g._id
                                  ? <CircularProgress size={14}/>
                                  : g.active_count === g.feed_count ? <PauseIcon fontSize="small"/> : <PlayArrowIcon fontSize="small"/>}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit"><IconButton size="small" onClick={()=>openEditGroup(g)}><EditIcon fontSize="small"/></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={()=>{setDelGrp(g);setReassignTo("");}}><DeleteIcon fontSize="small"/></IconButton></Tooltip>
                        </Box>
                      </Box>
                      <Stack direction="row" gap={0.8} flexWrap="wrap" sx={{mt:1.5}}>
                        <Chip label={`${g.feed_count||0} feeds`}   size="small" variant="outlined"/>
                        <Chip label={`${g.active_count||0} active`} size="small" variant="outlined" color="success"/>
                        <Chip label={g.poll_interval_seconds ? fmtInterval(g.poll_interval_seconds) : "Global poll"} size="small"/>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
      )}

      {/* ── FEEDS TAB ───────────────────────────────────────────────────────── */}
      {tab === 1 && (
        <Paper elevation={0} sx={{ border:"1px solid", borderColor:"divider", borderRadius:3 }}>
          <Box sx={{ p:2, borderBottom:"1px solid", borderColor:"divider", display:"flex", gap:1.5, flexWrap:"wrap" }}>
            <TextField size="small" placeholder="Search by name or URL…" value={feedSearch}
              onChange={e=>setFeedSearch(e.target.value)}
              InputProps={{ startAdornment:<InputAdornment position="start"><SearchIcon sx={{fontSize:18,color:"text.secondary"}}/></InputAdornment> }}
              sx={{ flex:1, minWidth:200 }}/>
            <FormControl size="small" sx={{ minWidth:150 }}>
              <InputLabel>Group</InputLabel>
              <Select value={feedGroup} label="Group" onChange={e=>{setFeedGroup(e.target.value);setFeedPage(0);}}>
                <MenuItem value="">All groups</MenuItem>
                {groups.map(g=><MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth:120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={feedActive} label="Status" onChange={e=>{setFeedActive(e.target.value);setFeedPage(0);}}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{fontWeight:600}}>Source</TableCell>
                  <TableCell sx={{fontWeight:600}}>Group</TableCell>
                  <TableCell sx={{fontWeight:600}}>Poll</TableCell>
                  <TableCell sx={{fontWeight:600}}>Status</TableCell>
                  <TableCell sx={{fontWeight:600}}>Today</TableCell>
                  <TableCell sx={{fontWeight:600}}>Fetched</TableCell>
                  <TableCell sx={{fontWeight:600}}>Err</TableCell>
                  <TableCell align="right" sx={{fontWeight:600}}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingFeeds
                  ? [...Array(8)].map((_,i)=>(
                      <TableRow key={i}>
                        {[...Array(8)].map((__,j)=><TableCell key={j}><Skeleton/></TableCell>)}
                      </TableRow>
                    ))
                  : visibleFeeds.length === 0
                    ? <TableRow>
                        <TableCell colSpan={8} align="center" sx={{py:6}}>
                          <RssFeedIcon sx={{fontSize:40,color:"text.disabled",opacity:.3}}/>
                          <Typography variant="body2" color="text.secondary" sx={{mt:1}}>No feeds found</Typography>
                        </TableCell>
                      </TableRow>
                    : visibleFeeds.map(f=>(
                        <TableRow key={f._id} hover>
                          <TableCell sx={{maxWidth:240}}>
                            <Typography variant="body2" fontWeight={500} noWrap>{f.title}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{maxWidth:220}}>{f.url}</Typography>
                          </TableCell>
                          <TableCell>
                            {f.group_id && (
                              <Chip size="small" label={f.group_id.name || "—"}
                                sx={{ bgcolor: (f.group_id.color||"#3b82f6")+"22", color: f.group_id.color||"primary.main", fontWeight:600, fontSize:"0.7rem" }}/>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {f.poll_random && <Tooltip title="Random polling within interval"><span style={{marginRight:3,opacity:.7}}>~</span></Tooltip>}
                              {fmtInterval(f.poll_interval_mode==="individual"?f.poll_interval_seconds:null)}{" "}
                              {f.poll_interval_mode!=="individual"&&`(${f.poll_interval_mode})`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={f.is_active?"Active":"Inactive"} color={f.is_active?"success":"default"}/>
                          </TableCell>
                          <TableCell><Typography variant="body2">{f.articles_today||0}</Typography></TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{fromNow(f.last_fetched_at)}</Typography></TableCell>
                          <TableCell>
                            {f.error_count > 0
                              ? <Chip size="small" label={f.error_count} color="error" variant="outlined"/>
                              : <Typography variant="caption" color="text.disabled">0</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" justifyContent="flex-end" gap={0.5}>
                              <Tooltip title={f.is_active?"Disable":"Enable"}>
                                <IconButton size="small" onClick={()=>handleToggleFeed(f)}
                                  sx={{color: f.is_active?"success.main":"text.disabled"}}>
                                  <PowerSettingsNewIcon sx={{fontSize:18}}/>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={()=>history.push(`/edit/feed/${f._id}`)}>
                                  <EditIcon fontSize="small"/>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Fetch Now">
                                <IconButton size="small" color="primary"
                                  onClick={() => handleFetchNow(f)}
                                  disabled={fetchingFeed === f._id}>
                                  {fetchingFeed === f._id
                                    ? <CircularProgress size={14}/>
                                    : <RefreshIcon fontSize="small"/>}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={()=>setDelFeed(f)}>
                                  <DeleteIcon fontSize="small"/>
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                }
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={feedTotal} page={feedPage} rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[50]} onPageChange={(_,p)=>setFeedPage(p)}
            sx={{ borderTop:"1px solid", borderColor:"divider" }}/>
        </Paper>
      )}

      {/* ── Add / Edit Group Dialog ──────────────────────────────────────────── */}
      <Dialog open={grpDialog} onClose={()=>setGrpDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editGrp ? "Edit Group" : "Add Group"}</DialogTitle>
        <DialogContent sx={{display:"flex",flexDirection:"column",gap:2,pt:"16px !important"}}>
          <TextField label="Group name *" size="small" value={grpForm.name} onChange={e=>setGrpForm(p=>({...p,name:e.target.value}))}/>
          <TextField label="Description" size="small" value={grpForm.description} onChange={e=>setGrpForm(p=>({...p,description:e.target.value}))}/>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{mb:0.8,display:"block"}}>Group colour</Typography>
            <Stack direction="row" gap={0.8} flexWrap="wrap">
              {GROUP_COLORS.map(c=>(
                <Box key={c} onClick={()=>setGrpForm(p=>({...p,color:c}))}
                  sx={{ width:22,height:22,borderRadius:"50%",bgcolor:c,cursor:"pointer",
                    border: grpForm.color===c ? "3px solid white" : "3px solid transparent",
                    outline: grpForm.color===c ? `2px solid ${c}` : "none",
                  }}/>
              ))}
            </Stack>
          </Box>
          <FormControl size="small" fullWidth>
            <InputLabel>Poll interval</InputLabel>
            <Select value={grpForm.poll_interval_seconds ?? ""} label="Poll interval"
              onChange={e=>setGrpForm(p=>({...p,poll_interval_seconds:e.target.value===''?null:e.target.value}))}>
              {POLL_OPTIONS.map(o=><MenuItem key={String(o.value)} value={o.value??""} >{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setGrpDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveGroup} disabled={savingGrp||!grpForm.name.trim()}>
            {savingGrp ? <CircularProgress size={16}/> : (editGrp?"Save":"Create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Group Dialog ──────────────────────────────────────────────── */}
      {delGrp && (
        <Dialog open onClose={()=>{setDelGrp(null);setReassignTo("");}}>
          <DialogTitle>Delete Group "{delGrp.name}"?</DialogTitle>
          <DialogContent>
            {delGrp.feed_count > 0 ? (
              <>
                <Alert severity="warning" sx={{mb:2}}>This group has {delGrp.feed_count} feed(s).</Alert>
                <FormControl size="small" fullWidth>
                  <InputLabel>Reassign feeds to</InputLabel>
                  <Select value={reassignTo} label="Reassign feeds to"
                    onChange={e=>setReassignTo(e.target.value)}>
                    <MenuItem value=""><em>Delete feeds too</em></MenuItem>
                    {groups.filter(g=>g._id!==delGrp._id).map(g=><MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </>
            ) : <Typography>This cannot be undone.</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>{setDelGrp(null);setReassignTo("");}}>Cancel</Button>
            <Button variant="contained" color="error" disabled={deletingGrp} onClick={confirmDeleteGroup}>
              {deletingGrp?<CircularProgress size={16}/>:"Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Delete Feed Dialog ───────────────────────────────────────────────── */}
      {delFeed && (
        <Dialog open onClose={()=>setDelFeed(null)}>
          <DialogTitle>Delete feed?</DialogTitle>
          <DialogContent><Typography>Delete <b>{delFeed.title}</b>? This cannot be undone.</Typography></DialogContent>
          <DialogActions>
            <Button onClick={()=>setDelFeed(null)}>Cancel</Button>
            <Button variant="contained" color="error" disabled={deletingFeed} onClick={confirmDeleteFeed}>
              {deletingFeed?<CircularProgress size={16}/>:"Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Import Confirm Dialog ─────────────────────────────────────────────── */}
      {importDialog && importData && (
        <Dialog open onClose={()=>{ setImportDialog(false); setImportData(null); }} maxWidth="xs" fullWidth>
          <DialogTitle>Import Feeds</DialogTitle>
          <DialogContent sx={{pt:"16px !important"}}>
            <Alert severity="info" sx={{mb:2}}>
              <b>{importData.feeds.length}</b> feed{importData.feeds.length!==1?"s":""} found in file.
              {importData.exported_at && (
                <> Exported {new Date(importData.exported_at).toLocaleDateString()}.</>
              )}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Feeds with duplicate URLs will be skipped. Missing groups will be created automatically.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>{ setImportDialog(false); setImportData(null); }}>Cancel</Button>
            <Button variant="contained" startIcon={importing?<CircularProgress size={16} color="inherit"/>:<FileUploadIcon/>}
              onClick={handleImportConfirm} disabled={importing}>
              {importing ? "Importing…" : "Import"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Base>
  );
};

export default FeedSources;
